import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { CulturalActivityConfiguration } from "@common/ADAPT.Common.Model/organisation/cultural-activity-configuration";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ConfigurationService } from "@org-common/lib/configuration/configuration.service";
import { CulturalActivityService } from "@org-common/lib/cultural-activity/cultural-activity.service";
import { ValueChangedEvent } from "devextreme/ui/number_box";
import { EMPTY, lastValueFrom, of, startWith, switchMap } from "rxjs";
import { tap } from "rxjs/operators";

@Component({
    selector: "adapt-configure-cadence-frequency",
    templateUrl: "./configure-cadence-frequency.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ConfigureCadenceFrequencyComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public type!: CulturalActivityType;
    @Input() public teamId?: number;

    @Input() public title = "Catch-up frequency";
    @Input() public cancelsFutureCatchups = false;

    public culturalActivityConfiguration?: CulturalActivityConfiguration;

    public constructor(
        private culturalActivityService: CulturalActivityService,
        private commonDataService: CommonDataService,
        private configurationService: ConfigurationService,
    ) {
        super();
    }

    public async ngOnInit() {
        // make sure we update the config should the user cancel changes
        this.configurationService.initialiseConfigPage$.pipe(
            startWith(undefined),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateData());
    }

    public async updateData() {
        this.culturalActivityConfiguration = await lastValueFrom(this.culturalActivityService.getCulturalActivityConfiguration(this.type, this.teamId));
        if (!this.culturalActivityConfiguration) {
            // no team config, use org config
            this.culturalActivityConfiguration = await lastValueFrom(this.culturalActivityService.getCulturalActivityConfiguration(this.type));
        }
    }

    @Autobind
    public resetToOrganisationDefault() {
        if (!this.culturalActivityConfiguration) {
            return EMPTY;
        }

        return of(this.culturalActivityConfiguration).pipe(
            switchMap((config) => this.commonDataService.remove(config)),
            switchMap(() => this.culturalActivityService.getCulturalActivityConfiguration(this.type)),
            tap((config) => this.culturalActivityConfiguration = config),
        );
    }

    public async onValueChanged(event: ValueChangedEvent) {
        // value not changed by user
        if (!event.event) {
            return;
        }

        if (this.culturalActivityConfiguration && !this.culturalActivityConfiguration.teamId && this.teamId) {
            // need to create a new config for this team
            this.culturalActivityConfiguration = await lastValueFrom(this.culturalActivityService.createCulturalActivityConfiguration(this.type, event.value, this.culturalActivityConfiguration.organisationId, this.teamId));
        }

        if (this.culturalActivityConfiguration) {
            this.culturalActivityConfiguration.frequencyMonths = event.value;
        }
    }
}
